import React, { useState, useEffect, useCallback, memo, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ProductSearchInput, { productLabel } from '../Form/ProductSearchInput';
import './EditableQuoteLines.css';

// Memoized table row component - only re-renders when its specific line changes
const QuoteLineRow = memo(({
  line,
  index,
  editMode,
  loading,
  onLineChange,
  onRemoveLine
}) => {
  const { t } = useTranslation('sales');
  // Calculate line total
  const lineTotal = useMemo(() => {
    const quantity = parseFloat(line.quantity || 0);
    const unitPrice = parseFloat(line.unit_price || 0);
    const discountPct = parseFloat(line.discount_percentage || 0);
    const taxPct = parseFloat(line.tax_percentage || 0);

    const subtotal = quantity * unitPrice;
    const discount = subtotal * (discountPct / 100);
    const afterDiscount = subtotal - discount;
    const tax = afterDiscount * (taxPct / 100);

    return afterDiscount + tax;
  }, [line.quantity, line.unit_price, line.discount_percentage, line.tax_percentage]);

  const selectedLabel = line.product_code ? productLabel(line) : (line.product_name || '');
  // A line already converted to a sales order keeps product, price, discount and tax, cannot go
  // below the converted quantity and cannot be removed (the server enforces the same)
  const converted = parseFloat(line.quantity_converted || 0);
  const locked = converted > 0;
  const lockedTitle = locked ? t('quotations.view.messages.convertedLineLocked', { quantity: converted.toLocaleString() }) : undefined;

  return (
    <tr className={line.isNew ? 'new-line' : line.isModified ? 'modified-line' : ''} title={lockedTitle}>
      <td>
        {editMode ? (
          <input
            type="number"
            value={line.line_number || index + 1}
            onChange={(e) => onLineChange(index, 'line_number', e.target.value)}
            className="form-input-inline"
            style={{ width: '50px' }}
          />
        ) : (
          line.line_number || index + 1
        )}
      </td>
      <td>
        {editMode && !locked ? (
          <ProductSearchInput
            value={line.product_id}
            selectedLabel={selectedLabel}
            onSelect={(product) => onLineChange(
              index, 'product_id', product ? (product.product_id ?? product.id) : '', product
            )}
            placeholder={t('editableSalesOrderLines.placeholders.searchProducts')}
            ariaLabel={`${t('common:ui.product')} ${index + 1}`}
          />
        ) : (
          selectedLabel || line.description || '-'
        )}
      </td>
      <td>
        {editMode ? (
          <input
            type="text"
            value={line.description || ''}
            onChange={(e) => onLineChange(index, 'description', e.target.value)}
            className="form-input-inline"
            placeholder={t('editableSalesOrderLines.placeholders.lineDescription')}
          />
        ) : (
          line.description || '-'
        )}
      </td>
      <td>
        {editMode ? (
          <input
            type="number"
            value={line.quantity || ''}
            onChange={(e) => onLineChange(index, 'quantity', e.target.value)}
            className="form-input-inline"
            aria-label={`${t('invoices.view.fields.quantity')} ${index + 1}`}
            step="any"
            min={locked ? converted : 0}
            required
            style={{ width: '80px' }}
          />
        ) : (
          parseFloat(line.quantity || 0).toLocaleString()
        )}
      </td>
      <td className="text-center">
        <span className="uom-display">{line.unit_of_measure || 'PCS'}</span>
      </td>
      <td>
        {editMode && !locked ? (
          <input
            type="number"
            value={line.unit_price || ''}
            onChange={(e) => onLineChange(index, 'unit_price', e.target.value)}
            className="form-input-inline"
            step="0.01"
            min="0"
            required
            style={{ width: '100px' }}
          />
        ) : (
          `$${parseFloat(line.unit_price || 0).toFixed(2)}`
        )}
      </td>
      <td>
        {editMode && !locked ? (
          <input
            type="number"
            value={line.discount_percentage || ''}
            onChange={(e) => onLineChange(index, 'discount_percentage', e.target.value)}
            className="form-input-inline"
            step="0.01"
            min="0"
            max="100"
            style={{ width: '70px' }}
          />
        ) : (
          `${parseFloat(line.discount_percentage || 0).toFixed(1)}%`
        )}
      </td>
      <td>
        {editMode && !locked ? (
          <input
            type="number"
            value={line.tax_percentage || ''}
            onChange={(e) => onLineChange(index, 'tax_percentage', e.target.value)}
            className="form-input-inline"
            step="0.01"
            min="0"
            style={{ width: '70px' }}
          />
        ) : (
          `${parseFloat(line.tax_percentage || 0).toFixed(1)}%`
        )}
      </td>
      <td className="text-right">
        <strong>${lineTotal.toFixed(2)}</strong>
      </td>
      {editMode && (
        <td>
          <div className="action-buttons">
            <button
              onClick={() => onRemoveLine(index, line)}
              className="btn-action delete"
              title={lockedTitle || t('common:ui.remove')}
              disabled={loading || locked}
            >
              ×
            </button>
          </div>
        </td>
      )}
    </tr>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for memo
  return (
    prevProps.line === nextProps.line &&
    prevProps.index === nextProps.index &&
    prevProps.editMode === nextProps.editMode &&
    prevProps.loading === nextProps.loading
  );
});

QuoteLineRow.displayName = 'QuoteLineRow';

const EditableQuoteLines = ({ data, parentEntity, editMode, onLinesChange }) => {
  const { t } = useTranslation('sales');
  const [localLines, setLocalLines] = useState([]);
  const loading = false;
  const linesRef = useRef([]);
  const onLinesChangeRef = useRef(onLinesChange);

  // Update the ref when onLinesChange changes
  useEffect(() => {
    onLinesChangeRef.current = onLinesChange;
  }, [onLinesChange]);

  // Initialize lines
  useEffect(() => {
    const lines = (data || []).map(line => ({ ...line, isModified: false, isNew: false }));
    linesRef.current = lines;
    setLocalLines(lines);
  }, [data]);

  // Every user edit updates the table and tells the parent right away, so a Save clicked
  // immediately after typing always sends the current lines
  const commit = useCallback((newLines) => {
    linesRef.current = newLines;
    setLocalLines(newLines);
    if (onLinesChangeRef.current) onLinesChangeRef.current(newLines);
  }, []);

  const handleLineChange = useCallback((index, field, value, productData = null) => {
    const newLines = [...linesRef.current];
    newLines[index] = {
      ...newLines[index],
      [field]: value,
      isModified: !newLines[index].isNew
    };

    // Auto-populate product details when product is selected
    if (field === 'product_id') {
      if (!value) {
        newLines[index] = { ...newLines[index], product_code: '', product_name: '' };
      } else if (productData) {
        newLines[index] = {
          ...newLines[index],
          product_name: productData.product_name,
          product_code: productData.product_code,
          unit_price: productData.standard_price || productData.base_price || 0,
          unit_of_measure: productData.unit_of_measure || 'PCS',
          description: newLines[index].description || productData.description || productData.product_name
        };
      }
    }
    commit(newLines);
  }, [commit]);

  const handleAddLine = () => {
    const prev = linesRef.current;
    commit([...prev, {
      temp_id: `new_${Date.now()}_${prev.length}`,
      line_number: prev.length + 1,
      product_id: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      discount_percentage: 0,
      tax_percentage: 0,
      total_price: 0,
      isNew: true,
      isModified: false
    }]);
  };

  // Removing only takes the line out of the form; it is deleted when the quotation is saved
  const handleRemoveLine = (index) => {
    commit(linesRef.current.filter((_, i) => i !== index));
  };

  // Calculate totals
  const totals = useMemo(() => {
    return localLines.reduce((acc, line) => {
      const quantity = parseFloat(line.quantity || 0);
      const unitPrice = parseFloat(line.unit_price || 0);
      const discountPct = parseFloat(line.discount_percentage || 0);
      const taxPct = parseFloat(line.tax_percentage || 0);

      const subtotal = quantity * unitPrice;
      const discount = subtotal * (discountPct / 100);
      const afterDiscount = subtotal - discount;
      const tax = afterDiscount * (taxPct / 100);
      const lineTotal = afterDiscount + tax;

      acc.subtotal += subtotal;
      acc.discount += discount;
      acc.tax += tax;
      acc.total += lineTotal;

      return acc;
    }, { subtotal: 0, discount: 0, tax: 0, total: 0 });
  }, [localLines]);

  return (
    <div className="editable-quote-lines">
      <div className="lines-header">
        <h3>{t('quoteConversion.quoteLines')}</h3>
        {editMode && (
          <button
            className="btn-primary btn-sm"
            onClick={handleAddLine}
            disabled={loading}
          >
            + {t('invoices.view.buttons.addLine')}
          </button>
        )}
      </div>

      {localLines.length === 0 ? (
        <div className="no-lines">
          <p>{t('editableSalesOrderLines.noLines')}</p>
          {editMode && (
            <button className="btn-primary" onClick={handleAddLine}>
              {t('editableSalesOrderLines.addFirstLine')}
            </button>
          )}
        </div>
      ) : (
        <div className="lines-table-wrapper">
          <table className="lines-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>#</th>
                <th style={{ width: '200px' }}>{t('common:ui.product')}</th>
                <th>{t('common:ui.description')}</th>
                <th style={{ width: '80px' }}>{t('invoices.view.fields.quantity')}</th>
                <th style={{ width: '60px' }}>{t('editableQuoteLines.columns.uom')}</th>
                <th style={{ width: '100px' }}>{t('common:ui.unitPrice')}</th>
                <th style={{ width: '80px' }}>{t('invoices.view.fields.discount')}</th>
                <th style={{ width: '80px' }}>{t('invoices.view.fields.tax')}</th>
                <th style={{ width: '120px' }}>{t('common:ui.total')}</th>
                {editMode && <th style={{ width: '80px' }}>{t('common:ui.actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {localLines.map((line, index) => (
                <QuoteLineRow
                  key={line.line_id || line.temp_id || index}
                  line={line}
                  index={index}
                  editMode={editMode}
                  loading={loading}
                  onLineChange={handleLineChange}
                  onRemoveLine={handleRemoveLine}
                />
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan={editMode ? 7 : 6} className="text-right">
                  <strong>{t('invoices.view.fields.subtotal')}:</strong>
                </td>
                <td className="text-right">
                  <strong>${totals.subtotal.toFixed(2)}</strong>
                </td>
                {editMode && <td></td>}
              </tr>
              {totals.discount > 0 && (
                <tr className="total-row">
                  <td colSpan={editMode ? 7 : 6} className="text-right">
                    {t('invoices.view.fields.discount')}:
                  </td>
                  <td className="text-right">
                    -${totals.discount.toFixed(2)}
                  </td>
                  {editMode && <td></td>}
                </tr>
              )}
              {totals.tax > 0 && (
                <tr className="total-row">
                  <td colSpan={editMode ? 7 : 6} className="text-right">
                    {t('invoices.view.fields.tax')}:
                  </td>
                  <td className="text-right">
                    ${totals.tax.toFixed(2)}
                  </td>
                  {editMode && <td></td>}
                </tr>
              )}
              <tr className="total-row grand-total">
                <td colSpan={editMode ? 7 : 6} className="text-right">
                  <strong>{t('common:ui.total')}:</strong>
                </td>
                <td className="text-right">
                  <strong className="total-amount">${totals.total.toFixed(2)}</strong>
                </td>
                {editMode && <td></td>}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default EditableQuoteLines;